import { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Send, 
  MessageSquare, 
  Mail,
  Clock,
  CheckCheck,
  Plus,
  ArrowLeft
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MessagesPage() {
  const { currentTheme } = useTheme();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newMessage, setNewMessage] = useState({
    subject: '',
    content: ''
  });

  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`, { withCredentials: true });
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/messages`, newMessage, { withCredentials: true });
      toast.success('Message sent successfully!');
      setShowCompose(false);
      setNewMessage({ subject: '', content: '' });
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (messageId) => {
    if (!replyContent.trim()) return;
    setSubmitting(true);

    try {
      await axios.post(`${API}/messages/${messageId}/reply`, 
        { subject: '', content: replyContent },
        { withCredentials: true }
      );
      toast.success('Reply sent!');
      setReplyContent('');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`, {}, { withCredentials: true });
      fetchMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Group messages by thread (subject)
  const groupedMessages = messages.reduce((acc, msg) => {
    const key = msg.reply_to || msg.message_id;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(msg);
    return acc;
  }, {});

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.background }}>
      <Navbar />
      
      <main className="pt-28 pb-16 px-4 md:px-8">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-medium" style={{ color: currentTheme.foreground }}>
                Messages
              </h1>
              <p className="font-body opacity-70 mt-1">
                Communicate with our coaching team or request deletion of your account and/or data in the Freedom On Fire app.
              </p>
            </div>
            <Dialog open={showCompose} onOpenChange={setShowCompose}>
              <DialogTrigger asChild>
                <Button 
                  style={{ backgroundColor: currentTheme.primary }}
                  data-testid="compose-message-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Message
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">New Message</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSendMessage} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Input
                      placeholder="What's this about?"
                      value={newMessage.subject}
                      onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                      required
                      data-testid="message-subject"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea
                      placeholder="Type your message..."
                      value={newMessage.content}
                      onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                      rows={6}
                      required
                      data-testid="message-content"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    style={{ backgroundColor: currentTheme.primary }}
                    disabled={submitting}
                    data-testid="send-message-btn"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Message List */}
            <div 
              className="md:col-span-1 rounded-2xl overflow-hidden"
              style={{ backgroundColor: 'white', boxShadow: '0 4px 30px rgba(0,0,0,0.05)' }}
            >
              <div 
                className="p-4 border-b font-body font-medium"
                style={{ borderColor: currentTheme.border, color: currentTheme.foreground }}
              >
                Inbox ({messages.length})
              </div>
              <div className="divide-y max-h-[600px] overflow-y-auto" style={{ borderColor: currentTheme.border }}>
                {loading ? (
                  <div className="p-4 space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="skeleton h-16 rounded-xl" />
                    ))}
                  </div>
                ) : messages.length > 0 ? (
                  messages.filter(m => !m.reply_to).map((message) => (
                    <button
                      key={message.message_id}
                      onClick={() => {
                        setSelectedMessage(message);
                        if (!message.is_read && message.sender_id !== user?.user_id) {
                          markAsRead(message.message_id);
                        }
                      }}
                      className={`w-full p-4 text-left transition-colors hover:bg-gray-50 ${
                        selectedMessage?.message_id === message.message_id ? 'bg-gray-50' : ''
                      }`}
                      data-testid={`message-item-${message.message_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${currentTheme.primary}20` }}
                        >
                          <span className="font-medium" style={{ color: currentTheme.primary }}>
                            {message.sender_name?.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span 
                              className={`font-body ${!message.is_read && message.sender_id !== user?.user_id ? 'font-semibold' : ''}`}
                              style={{ color: currentTheme.foreground }}
                            >
                              {message.sender_name}
                            </span>
                            <span className="text-xs opacity-50 flex-shrink-0">
                              {formatDate(message.created_at)}
                            </span>
                          </div>
                          <p className="font-body text-sm font-medium truncate" style={{ color: currentTheme.foreground }}>
                            {message.subject}
                          </p>
                          <p className="font-body text-xs opacity-60 truncate">
                            {message.content}
                          </p>
                        </div>
                        {!message.is_read && message.sender_id !== user?.user_id && (
                          <span 
                            className="w-2 h-2 rounded-full flex-shrink-0 mt-2"
                            style={{ backgroundColor: currentTheme.primary }}
                          />
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-body opacity-60">No messages yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Message Detail */}
            <div 
              className="md:col-span-2 rounded-2xl"
              style={{ backgroundColor: 'white', boxShadow: '0 4px 30px rgba(0,0,0,0.05)' }}
            >
              {selectedMessage ? (
                <div className="h-full flex flex-col">
                  {/* Header */}
                  <div className="p-6 border-b" style={{ borderColor: currentTheme.border }}>
                    <button 
                      onClick={() => setSelectedMessage(null)}
                      className="md:hidden flex items-center gap-2 mb-4 font-body text-sm opacity-70"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </button>
                    <h2 className="font-display text-xl font-medium" style={{ color: currentTheme.foreground }}>
                      {selectedMessage.subject}
                    </h2>
                    <div className="flex items-center gap-4 mt-2 text-sm opacity-60">
                      <span>From: {selectedMessage.sender_name}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(selectedMessage.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 p-6 overflow-y-auto space-y-6 max-h-[400px]">
                    {/* Original message */}
                    <div className={`flex ${selectedMessage.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}>
                      <div 
                        className={`max-w-[80%] rounded-2xl p-4 ${
                          selectedMessage.sender_id === user?.user_id 
                            ? 'rounded-br-md' 
                            : 'rounded-bl-md'
                        }`}
                        style={{ 
                          backgroundColor: selectedMessage.sender_id === user?.user_id 
                            ? currentTheme.primary 
                            : currentTheme.muted,
                          color: selectedMessage.sender_id === user?.user_id ? 'white' : currentTheme.foreground
                        }}
                      >
                        <p className="font-body whitespace-pre-wrap">{selectedMessage.content}</p>
                        <div className="flex items-center justify-end gap-1 mt-2 text-xs opacity-70">
                          {formatDate(selectedMessage.created_at)}
                          {selectedMessage.sender_id === user?.user_id && selectedMessage.is_read && (
                            <CheckCheck className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Replies */}
                    {messages
                      .filter(m => m.reply_to === selectedMessage.message_id)
                      .map((reply) => (
                        <div 
                          key={reply.message_id}
                          className={`flex ${reply.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div 
                            className={`max-w-[80%] rounded-2xl p-4 ${
                              reply.sender_id === user?.user_id 
                                ? 'rounded-br-md' 
                                : 'rounded-bl-md'
                            }`}
                            style={{ 
                              backgroundColor: reply.sender_id === user?.user_id 
                                ? currentTheme.primary 
                                : currentTheme.muted,
                              color: reply.sender_id === user?.user_id ? 'white' : currentTheme.foreground
                            }}
                          >
                            <p className="font-body whitespace-pre-wrap">{reply.content}</p>
                            <div className="flex items-center justify-end gap-1 mt-2 text-xs opacity-70">
                              {formatDate(reply.created_at)}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* Reply Input */}
                  <div className="p-4 border-t" style={{ borderColor: currentTheme.border }}>
                    <div className="flex gap-3">
                      <Textarea
                        placeholder="Type your reply..."
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        rows={2}
                        className="flex-1"
                        data-testid="reply-input"
                      />
                      <Button
                        onClick={() => handleReply(selectedMessage.message_id)}
                        disabled={!replyContent.trim() || submitting}
                        style={{ backgroundColor: currentTheme.primary }}
                        data-testid="send-reply-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center p-12">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-20" />
                    <h3 className="font-display text-xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
                      Select a message
                    </h3>
                    <p className="font-body opacity-60">
                      Choose a conversation from the list to view details
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
